import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "电视剧", to: "/category/tv" },
  { label: "电影", to: "/category/movie" },
  { label: "综艺", to: "/category/variety" },
  { label: "资讯", to: "/news" },
];

export default function Header() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = keyword.trim();
    if (!query) return;
    navigate(`/search?q=${encodeURIComponent(query)}`);
    setMenuOpen(false);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
      isActive
        ? "text-accent-600"
        : "text-foreground-700 hover:text-accent-600"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-secondary-200 bg-background-50/95 backdrop-blur">
      <div className="hidden w-full bg-primary-950 md:block">
        <div className="mx-auto flex max-w-[1280px] items-center justify-between px-4 md:px-6 py-1.5 text-xs text-primary-100">
          <span className="flex items-center gap-1.5">
            <i className="ri-star-smile-line text-accent-400"></i>
            星空传媒 · 优质影视内容聚合平台
          </span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-database-2-line"></i>
              当前为演示数据模式
            </span>
            <Link
              to="/news"
              className="text-primary-100 transition-colors hover:text-accent-400 cursor-pointer"
            >
              影视资讯
            </Link>
          </span>
        </div>
      </div>

      <div className="mx-auto flex max-w-[1280px] items-center gap-4 px-4 md:px-6 h-14 md:h-16">
        <Link to="/" className="flex shrink-0 items-center gap-2 cursor-pointer">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-600 text-white">
            <i className="ri-movie-2-line text-xl"></i>
          </span>
          <span className="font-heading text-lg md:text-xl font-extrabold tracking-tight text-foreground-950">
            星空<span className="text-accent-500">传媒</span>
          </span>
        </Link>

        <nav className="ml-2 hidden items-center gap-1 md:flex">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"} className={navLinkClass}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form
          onSubmit={handleSearch}
          className="ml-auto hidden w-64 items-center gap-2 rounded-full border border-secondary-200 bg-secondary-50 px-3 py-1.5 transition-colors focus-within:border-accent-300 md:flex lg:w-72"
        >
          <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            type="text"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索影视作品或资讯"
            aria-label="搜索影视作品或资讯"
            className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
          <button
            type="submit"
            className="shrink-0 whitespace-nowrap rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-accent-600 cursor-pointer"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((prev) => !prev)}
          className="ml-auto flex h-9 w-9 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-secondary-100 md:hidden cursor-pointer"
        >
          <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-secondary-200 bg-background-50 px-4 py-4 md:hidden">
          <form onSubmit={handleSearch} className="mb-4 flex items-center gap-2 rounded-full border border-secondary-200 bg-secondary-50 px-3 py-2">
            <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视作品或资讯"
              aria-label="搜索影视作品或资讯"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="shrink-0 whitespace-nowrap rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-white cursor-pointer"
            >
              搜索
            </button>
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-4 py-3 text-sm font-medium transition-colors cursor-pointer ${
                    isActive
                      ? "bg-accent-100 text-accent-700"
                      : "bg-secondary-50 text-foreground-800"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}